import { useContext, useState } from "react";
import { useParams } from "react-router-dom";

import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";

import { LoansContext } from "../../context/LoansContext";
import { RootContext } from "../../context/RootContext";
import { fetchFieldAuditHistory } from "../../services/network";
import { AUDIT_RESOURCE_TYPE } from "../../constants/auditFields";

type AuditTagProps = {
  field: string;
};

// Icon shown next to a field's label once it has any audit history. The
// last-3 entries are fetched lazily on hover (there's no bulk "full
// history" endpoint) and cached in context so repeat hovers don't refetch.
export default function AuditTag({ field }: AuditTagProps) {
  const { loanId } = useParams<{ loanId: string }>();
  const { fieldAuditHistoryCache, setFieldAuditHistoryCache } = useContext(LoansContext);
  const { dynamicConfig } = useContext(RootContext);
  const [loading, setLoading] = useState(false);

  const history = fieldAuditHistoryCache?.[field];
  const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };

  const handleFetchHistory = async () => {
    if (history || loading || !loanId) return;
    setLoading(true);
    try {
      const { data } = await fetchFieldAuditHistory(
        AUDIT_RESOURCE_TYPE,
        loanId,
        field,
        3,
        0,
        realmHeaders,
      );
      const entries = Array.isArray(data) ? data : data?.data ?? data?.results ?? [];
      setFieldAuditHistoryCache?.((prev: Record<string, any[]>) => ({
        ...prev,
        [field]: entries,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderTooltipContent = () => {
    if (loading) return "Loading...";
    if (!history?.length) return "No audit history";
    return (
      <div>
        {history.slice(0, 3).map((entry: any, idx: number) => (
          <div key={idx}>{entry?.comment || String(entry?.value ?? "")}</div>
        ))}
      </div>
    );
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      onOpen={handleFetchHistory}
      arrow
      placement="right-start"
      slots={{ transition: Fade }}
      slotProps={{ transition: { timeout: 600 } }}
    >
      <HistoryEduOutlinedIcon className="audit-tag-icon" fontSize="small" />
    </Tooltip>
  );
}
